import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Card, Divider, Empty, Spin, Statistic, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { InfoCircleOutlined } from '@ant-design/icons';

const fmt = (num) => {
    if (num === null || num === undefined) return '-';
    const n = Number(num);
    if (Number.isNaN(n)) return '-';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const SERIES_COLORS = {
    gridImport: '#1890ff',
    solarToSite: '#fa8c16',
    gridExport: '#8c8c8c',
    transformer: '#fadb14',
};

const SummaryBox = ({ title, value, suffix }) => (
    <Card className="summaryBox">
        <Statistic title={title} value={fmt(value)} suffix={suffix} />
    </Card>
);

const UtilizationChart = ({
    loading,
    series = [],
    totals = {},
}) => {
    const hourlyData = useMemo(() => {
        if (!series || series.length === 0) return [];

        const buckets = {};
        for (let i = 0; i < 24; i++) {
            buckets[i] = { count: 0, gridImportKWh: 0, solarToSiteKWh: 0, gridExportKWh: 0, transformerLoading: 0, solarGenKWh: 0 };
        }

        series.forEach((item) => {
            const h = dayjs(item.ts).hour();
            if (buckets[h]) {
                buckets[h].count++;
                buckets[h].gridImportKWh += Number(item.gridImportKWh || 0);
                buckets[h].solarToSiteKWh += Number(item.solarToSiteKWh || 0);
                buckets[h].gridExportKWh += Number(item.gridExportKWh || 0);
                buckets[h].transformerLoading += Number(item.transformerLoading || 0);
                buckets[h].solarGenKWh += Number(item.solarGenKWh || 0);
            }
        });

        const result = [];
        for (let i = 0; i < 24; i++) {
            const b = buckets[i];
            const count = b.count || 1;
            const hasData = b.count > 0;
            result.push({
                label: `${String(i).padStart(2, '0')}:00`,
                gridImportKWh: hasData ? b.gridImportKWh : null,
                solarToSiteKWh: hasData ? b.solarToSiteKWh : null,
                gridExportKWh: hasData ? b.gridExportKWh : null,
                transformerLoading: hasData ? b.transformerLoading / count : null,
                solarGenKWh: hasData ? b.solarGenKWh : null,
            });
        }
        return result;
    }, [series]);

    const chartOption = useMemo(() => {
        if (!hourlyData || hourlyData.length === 0) {
            return null;
        }
        const xLabels = hourlyData.map((item) => item.label);
        const toData = (key) => hourlyData.map((item) => item[key] || 0);

        const formatTooltip = (params) => {
            if (!params || !params.length) return '';

            const lines = [params[0].axisValue];
            params.forEach((p) => {
                const numericVal = Number(p.data);
                const displayVal = Number.isNaN(numericVal)
                    ? '-'
                    : numericVal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                lines.push(`${p.marker} ${p.seriesName}: ${displayVal}%`);
            });

            return lines.join('<br/>');
        };

        return {
            color: [SERIES_COLORS.gridImport, SERIES_COLORS.solarToSite, SERIES_COLORS.gridExport, SERIES_COLORS.transformer],
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
                formatter: formatTooltip,
            },
            legend: {
                data: ['Grid Import', 'Solar to Site', 'Grid Export', 'Transformer Loading'],
                bottom: 0,
                icon: 'rect',
                itemHeight: 2,
                itemWidth: 15,
                itemGap: 20,
            },
            grid: {
                left: 50,
                right: 50,
                bottom: 60,
                top: 30,
            },
            xAxis: {
                type: 'category',
                data: xLabels,
                boundaryGap: false,
                axisLine: { show: false },
                axisTick: { show: false },
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'kWh',
                    position: 'left',
                    splitLine: {
                        lineStyle: { type: 'dashed', color: '#E8E8E8' }
                    },
                    axisLine: { show: false },
                    axisTick: { show: false },
                },
                {
                    type: 'value',
                    name: '%',
                    position: 'right',
                    min: 0,
                    max: 100,
                    axisLabel: { formatter: '{value}%' },
                    splitLine: { show: false },
                    axisLine: { show: false },
                    axisTick: { show: false },
                }
            ],
            series: [
                {
                    name: 'Grid Import',
                    type: 'line',
                    smooth: false,
                    data: toData('gridImportKWh'),
                    lineStyle: { color: SERIES_COLORS.gridImport, width: 2 }, // Blue
                    itemStyle: { color: SERIES_COLORS.gridImport },
                    showSymbol: false,
                },
                {
                    name: 'Solar to Site',
                    type: 'line',
                    smooth: false,
                    data: toData('solarToSiteKWh'),
                    lineStyle: { color: SERIES_COLORS.solarToSite, width: 2 }, // Orange
                    itemStyle: { color: SERIES_COLORS.solarToSite },
                    showSymbol: false,
                },
                {
                    name: 'Grid Export',
                    type: 'line',
                    smooth: false,
                    data: toData('gridExportKWh'),
                    lineStyle: { color: SERIES_COLORS.gridExport, width: 2 }, // Grey
                    itemStyle: { color: SERIES_COLORS.gridExport },
                    showSymbol: false,
                },
                {
                    name: 'Transformer Loading',
                    type: 'line',
                    smooth: false,
                    yAxisIndex: 1,
                    data: hourlyData.map((item) => {
                        const val = item.transformerLoading;
                        return val != null ? (Number(val) * 100).toFixed(2) : 0;
                    }),
                    lineStyle: { color: SERIES_COLORS.transformer, width: 2 }, // Yellow
                    itemStyle: { color: SERIES_COLORS.transformer },
                    showSymbol: false,
                },
            ],
        };
    }, [hourlyData]);

    const loadingOption = {
        color: '#00B855',
    };

    const emptyOption = {
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
    };

    return (
        <div className="utilizationChart">
            <div className="chart">
                <div className="summaryBoxes">
                    <SummaryBox title="Grid Import" value={totals.gridImportKWh} suffix="kWh" />
                    <Divider type="vertical" />
                    <SummaryBox title="Grid Export" value={totals.gridExportKWh} suffix="kWh" />
                    <Divider type="vertical" />
                    <SummaryBox title="Solar Generated" value={totals.solarGenKWh} suffix="kWh" />
                    <Divider type="vertical" />
                    <SummaryBox title="Solar to Site" value={totals.solarToSiteKWh} suffix="kWh" />
                    <Divider type="vertical" />
                    <Tooltip title="Average transformer loading for site grid meters">
                        <SummaryBox
                            title={<span>Transformer Loading <InfoCircleOutlined /></span>}
                            value={totals.avgTransformerLoading == null ? null : totals.avgTransformerLoading * 100}
                            suffix="%"
                        />
                    </Tooltip>
                </div>
                <div className="chartBody">
                    {chartOption ? (
                        <ReactEcharts
                            option={chartOption}
                            style={{ height: 480, width: '100%' }}
                            notMerge
                            showLoading={loading}
                            loadingOption={loadingOption}
                        />
                    ) : loading ? (
                        <ReactEcharts
                            option={emptyOption}
                            style={{ height: 480, width: '100%' }}
                            notMerge
                            showLoading
                            loadingOption={loadingOption}
                        />
                    ) : (
                        <Empty description="No utilization data for this date" />
                    )}
                </div>
            </div>
        </div>
    );
};

export default UtilizationChart;
